// pages/admins/permissions/permissionUtils.js
//
// Single source of truth for the small pieces of shared logic/labels used
// by AccessManagementUsersPage.jsx and every PermissionXxx component below
// it -- previously duplicated only inside the page file. Nothing here
// changes what data is fetched or how an override is resolved; these are
// the exact same enums/helpers the page already relied on.

// The three override states a user's permission can be set to -- mirrors
// the ALLOW/DENY/INHERIT enum stored server-side (user_permission_overrides.
// effect + is_active).
export const SUPPORTED_SCOPES = ["SELF", "TEAM", "DEPARTMENT", "UNIT", "ALL"];

// PROJECT is a real, valid scope server-side, but only for the specific
// permissions the backend has already established it for (derived from
// existing role_permissions configuration -- see services/permissionService
// .js#isProjectScopeSupportedForPermission on the backend). It is NEVER
// added to SUPPORTED_SCOPES itself, since that list is also used by the
// module-wide "Allow All" bulk action (AccessManagementUsersPage.jsx),
// which applies ONE scope to every permission in a module -- offering
// PROJECT there could apply it to a permission that doesn't support it.
// Use this helper for a SINGLE permission row's own scope dropdown, which
// is exactly where the per-permission `supportsProjectScope` flag applies.
export function scopesForPermission(p) {
  return p?.supportsProjectScope ? [...SUPPORTED_SCOPES, "PROJECT"] : SUPPORTED_SCOPES;
}

// The architecture stores only the scope TYPE on the permission row -- the
// actual unit/department it resolves against is computed dynamically per
// user at request time (see services/dataScopeService.js's getScopeClause,
// which reads the ACTOR's own trusted unit_id/department_id, never a value
// stored on the permission itself). So this never invents a static unit/
// department name -- "User's Unit"/"User's Department" is the accurate
// description of how UNIT/DEPARTMENT scope actually resolves, for ANY user
// it's shown for.
export const SCOPE_LABELS = {
  SELF: "Self",
  TEAM: "User's Team",
  DEPARTMENT: "User's Department",
  UNIT: "User's Unit",
  PROJECT: "Project",
  ALL: "All",
};
export function scopeLabel(scope) {
  if (!scope) return null;
  return SCOPE_LABELS[scope] || scope;
}

// Compact variant for space-constrained spots -- same underlying meaning as
// scopeLabel, just shorter than "User's Unit".
export const SHORT_SCOPE_LABELS = { SELF: "Self", TEAM: "Team", DEPARTMENT: "Dept.", UNIT: "Unit", PROJECT: "Project", ALL: "All" };
export function shortScopeLabel(scope) {
  if (!scope) return null;
  return SHORT_SCOPE_LABELS[scope] || scope;
}

export const HIGH_RISK_MODULE_KEYS = new Set(["access_management"]);

export const STATE_OPTIONS = [
  { key: "INHERIT", label: "Inherit", icon: "fa-rotate-left", cls: "am-state-inherit" },
  { key: "ALLOW", label: "Allow", icon: "fa-circle-check", cls: "am-state-allow" },
  { key: "DENY", label: "Deny", icon: "fa-circle-xmark", cls: "am-state-deny" },
];

// Page-level status filter for the permission rows shown within each
// expanded module -- purely a client-side view filter over the already-
// fetched matrix, same convention as the module search.
export const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "ALLOWED", label: "Effective Allowed" },
  { key: "DENIED", label: "Effective Denied" },
  { key: "OVERRIDES", label: "Overrides Only" },
  { key: "INHERITED", label: "Inherited Only" },
];

// The permission's current saved override state (or "INHERIT" if it has
// none) -- the single source of truth for what a row's control shows.
export function currentState(original) {
  return original.override
    ? { state: original.override.effect, dataScope: original.override.dataScope, reason: original.override.reason || "", validFrom: original.override.validFrom || "", validUntil: original.override.validUntil || "" }
    : { state: "INHERIT", dataScope: null, reason: "", validFrom: "", validUntil: "" };
}

// Whether a cell is EFFECTIVELY allowed if `state` were applied -- used to
// live-preview the Effective badge while a draft state differs from what's
// actually saved. Mirrors the server's own ALLOW/DENY/INHERIT resolution:
// INHERIT falls back to the role grant (p.roleGranted) whenever it reverts
// a PREVIOUSLY SAVED override -- p.effectiveAllowed alone would still
// reflect that stale saved override in that one case.
export function cellIsAllowed(p, state) {
  if (state.state === "ALLOW") return true;
  if (state.state === "DENY") return false;
  if (!p.override) return !!p.effectiveAllowed;
  return !!p.roleGranted;
}

// Read-only counts for one module's permission list -- purely presentational
// aggregation over already-fetched rows (module card header, legend context),
// never a second source of truth for what is allowed/denied/overridden.
export function moduleCounts(permissions) {
  let allowed = 0, denied = 0, overrides = 0;
  permissions.forEach((p) => {
    if (p.effectiveAllowed) allowed += 1; else denied += 1;
    if (p.override) overrides += 1;
  });
  return { total: permissions.length, allowed, denied, overrides };
}
